/**
 * Reusable paywall modal. Pops up when a free user clicks a Pro-only feature.
 * Collects email/name (prefilled if logged in) and redirects to Stripe Checkout.
 */
import { useState } from 'react';
import { Lock, ArrowRight, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { createCertCheckout } from '@/lib/certAccess';

export default function CertPaywallModal({
  open,
  onOpenChange,
  certCode,
  certName,
  amount = 39,
  defaultEmail = '',
  defaultName = '',
  feature = 'this feature',
  simQuestionCount = 50,
}) {
  const proFeatures = [
    `Full ${simQuestionCount}-question Exam Simulation with timer`,
    'Personalized Weak-Areas practice',
    'AI Coach Chat (GPT-4o tutor) — unlimited messages',
    'All 250+ exam-grade questions unlocked',
    '2 months access — one-time payment',
  ];
  const [email, setEmail] = useState(defaultEmail);
  const [name, setName] = useState(defaultName);
  const [submitting, setSubmitting] = useState(false);

  const handleCheckout = async () => {
    if (!email || !name) {
      toast.error('Please enter your name and email');
      return;
    }
    setSubmitting(true);
    try {
      // Persist email locally so post-Stripe return + future visits resolve access
      try { localStorage.setItem('paywall_email', email.trim().toLowerCase()); } catch {}
      const data = await createCertCheckout({
        cert_code: certCode,
        customer_email: email,
        customer_name: name,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error('Could not start checkout. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="cert-paywall-modal">
        <DialogHeader>
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center mb-2">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <DialogTitle className="text-2xl">Unlock {certName}</DialogTitle>
          <DialogDescription>
            {feature} is part of <strong>Pro access</strong>. Unlock the full {certName}{' '}
            experience with a one-time payment.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-violet-100 bg-violet-50/60 p-4 space-y-2">
          {proFeatures.map((f) => (
            <div key={f} className="flex items-start gap-2 text-sm text-slate-700">
              <CheckCircle2 className="w-4 h-4 text-violet-600 mt-0.5 flex-shrink-0" />
              <span>{f}</span>
            </div>
          ))}
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="paywall-name">Full name</Label>
            <Input
              id="paywall-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              data-testid="paywall-name-input"
            />
          </div>
          <div>
            <Label htmlFor="paywall-email">Email (used to grant access)</Label>
            <Input
              id="paywall-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="paywall-email-input"
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-1">
          <div>
            <div className="text-3xl font-bold text-slate-900">${amount}</div>
            <div className="text-xs text-slate-500">One-time payment · 2 months access</div>
          </div>
          <Button
            onClick={handleCheckout}
            disabled={submitting}
            className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white px-6"
            data-testid="paywall-checkout-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting...
              </>
            ) : (
              <>
                Add to your new skill heights <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
        <p className="text-xs text-slate-500 text-center flex items-center justify-center gap-1.5" data-testid="paywall-stripe-trust">
          <Lock className="w-3 h-3" />
          Secured by Stripe <span className="text-slate-300">•</span> SCA-compliant
        </p>
      </DialogContent>
    </Dialog>
  );
}
